/**
 * MinibarModal — catalog picker for adding minibar charges to a booking.
 * Used from:
 *   - CheckInOutPage (reception/frontdesk) → pass bookingId
 *   - HousekeeperDashboard → pass roomId (backend resolves to active booking)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark, faWineBottle, faMinus, faPlus, faCheck,
  faBottleWater, faCandyCane, faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";
import "./MinibarModal.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface MinibarItem {
  id: number;
  name: string;
  category: string;
  price: number;
  description?: string;
  available: boolean;
}

interface Props {
  /** Pass bookingId for reception/checkout flow */
  bookingId?: string;
  /** Pass roomId for housekeeping flow (backend resolves to active booking) */
  roomId?: string;
  guestName?: string;
  roomNumber?: string;
  onClose: () => void;
  /** Called with new totals after successful charge */
  onCharged?: (totals: { extrasSum: number; newTotal: number }) => void;
}

const CAT_ICONS: Record<string, any> = {
  drinks:  faWineBottle,
  water:   faBottleWater,
  snacks:  faCandyCane,
  other:   faBoxOpen,
};

const CAT_COLOR: Record<string, string> = {
  drinks: "#818cf8",
  water:  "#38bdf8",
  snacks: "#fbbf24",
  other:  "#94a3b8",
};

export default function MinibarModal({ bookingId, roomId, guestName, roomNumber, onClose, onCharged }: Props) {
  const [items, setItems]       = useState<MinibarItem[]>([]);
  const [qtys, setQtys]         = useState<Record<number, number>>({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState(false);

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/minibar/items`, { headers })
      .then(r => setItems((r.data || []).filter((i: MinibarItem) => i.available)))
      .catch(() => setError("Failed to load minibar items"))
      .finally(() => setLoading(false));
  }, []);

  const setQty = (id: number, delta: number) => {
    setQtys(prev => {
      const next = Math.max(0, (prev[id] || 0) + delta);
      if (next === 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const selected = items.filter(it => (qtys[it.id] || 0) > 0);
  const total = selected.reduce((s, it) => s + it.price * (qtys[it.id] || 0), 0);

  const handleConfirm = async () => {
    if (!selected.length) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        items: selected.map(it => ({ name: it.name, price: it.price, qty: qtys[it.id] })),
        added_by: null,
      };
      let totals: any;
      if (bookingId) {
        const r = await axios.post(`${API}/api/minibar/charge/${bookingId}`, payload, { headers });
        totals = r.data.totals;
      } else if (roomId) {
        const r = await axios.post(`${API}/api/minibar/charge-by-room/${roomId}`, payload, { headers });
        totals = r.data.totals;
      }
      setSuccess(true);
      if (totals && onCharged) onCharged(totals);
      setTimeout(onClose, 1400);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || "Failed to add charges");
      setSaving(false);
    }
  };

  const categories = [...new Set(items.map(i => i.category))];

  return (
    <div className="mb-overlay" onClick={onClose}>
      <div className="mb-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mb-header">
          <div className="mb-header-left">
            <div className="mb-icon"><FontAwesomeIcon icon={faWineBottle} /></div>
            <div>
              <h2 className="mb-title">Minibar Charges</h2>
              <p className="mb-sub">
                {guestName && <span>{guestName}</span>}
                {roomNumber && <span> · Room {roomNumber}</span>}
                {!guestName && !roomNumber && <span>Select consumed items</span>}
              </p>
            </div>
          </div>
          <button className="mb-close" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>

        {/* Body */}
        <div className="mb-body">
          {loading && <div className="mb-loading">Loading catalog…</div>}

          {!loading && items.length === 0 && (
            <div className="mb-empty">
              <FontAwesomeIcon icon={faBoxOpen} className="mb-empty-icon" />
              <p>No minibar items configured.</p>
              <span>Ask a manager to set up the catalog.</span>
            </div>
          )}

          {!loading && categories.map(cat => {
            const catItems = items.filter(i => i.category === cat);
            const catColor = CAT_COLOR[cat] || CAT_COLOR.other;
            const catIcon  = CAT_ICONS[cat] || CAT_ICONS.other;
            return (
              <div key={cat} className="mb-category">
                <div className="mb-cat-label" style={{ color: catColor }}>
                  <FontAwesomeIcon icon={catIcon} /> {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </div>
                <div className="mb-items-grid">
                  {catItems.map(item => {
                    const qty = qtys[item.id] || 0;
                    return (
                      <div key={item.id} className={`mb-item ${qty > 0 ? "selected" : ""}`}>
                        <div className="mb-item-top">
                          <span className="mb-item-name">{item.name}</span>
                          {item.description && <span className="mb-item-desc">{item.description}</span>}
                        </div>
                        <div className="mb-item-bottom">
                          <span className="mb-item-price">EGP {Number(item.price).toLocaleString()}</span>
                          <div className="mb-qty-row">
                            <button className="mb-qty-btn" onClick={() => setQty(item.id, -1)} disabled={qty === 0}>
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                            <span className="mb-qty-num">{qty}</span>
                            <button className="mb-qty-btn" onClick={() => setQty(item.id, 1)}>
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mb-footer">
          {error && <div className="mb-error">{error}</div>}

          {success ? (
            <div className="mb-success">
              <FontAwesomeIcon icon={faCheck} /> Charges added — EGP {total.toLocaleString()}
            </div>
          ) : (
            <>
              <div className="mb-summary">
                {selected.length > 0 ? (
                  <span>{selected.length} item{selected.length > 1 ? "s" : ""} · <strong>EGP {total.toLocaleString()}</strong></span>
                ) : (
                  <span className="mb-summary-empty">No items selected</span>
                )}
              </div>
              <div className="mb-footer-actions">
                <button className="mb-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
                <button
                  className="mb-btn-confirm"
                  onClick={handleConfirm}
                  disabled={saving || selected.length === 0}
                >
                  {saving ? "Adding…" : `Add to Bill${selected.length ? ` · EGP ${total.toLocaleString()}` : ""}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
